// Images joined to a chat message or to a task (pièces jointes, SPEC §17 and §12): a 📎 button
// (it opens the file chooser), a pasted screenshot or a dropped file, thumbnails with × to take one
// back. At most MAX_IMAGES images, PNG / JPEG / WebP / GIF of IMAGE_MAX_BYTES at most each; they
// are sent as [{name, data}] (data: URL, base64) in the JSON body, and the server checks them again
// (their first bytes, not their name).
import { h, icon, setAttr } from '../dom.js';
import { t } from '../i18n/fr.js';

/** Images joined to one message or one task. */
export const MAX_IMAGES = 2;
/** Largest image (the server's limit too). */
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
/** `accept` of the file chooser. */
export const IMAGE_ACCEPT = IMAGE_TYPES.join(',');
/** Time to send a request carrying images (up to 2 × 10 Mo, base64). */
export const UPLOAD_TIMEOUT_MS = 2 * 60 * 1000;
const EXT_TYPES = Object.freeze({ png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' });

/**
 * The accepted image type of a file (pure): its declared type, else its extension (a dropped file
 * may come without a type); '' when it is not an accepted image.
 * @param {{name?: string, type?: string}} file
 */
export function imageTypeOf(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type) return IMAGE_TYPES.includes(type) ? type : '';
  const ext = /\.([a-z0-9]+)$/i.exec(String(file?.name || ''))?.[1]?.toLowerCase();
  return (ext && EXT_TYPES[ext]) || '';
}

/**
 * Why a file cannot be joined (French), or null (pure).
 * @param {{name?: string, type?: string, size?: number}} file
 * @param {number} count images already joined
 * @param {number} [max]
 * @returns {string|null}
 */
export function imageProblem(file, count, max = MAX_IMAGES) {
  const name = String(file?.name || 'image');
  if (count >= max) return t('attach.tooMany', { max });
  if (!imageTypeOf(file)) return t('attach.badType', { name });
  const size = Number(file?.size);
  if (Number.isFinite(size) && size > IMAGE_MAX_BYTES) return t('attach.tooBig', { name, max: IMAGE_MAX_BYTES / 1024 / 1024 });
  if (Number.isFinite(size) && size <= 0) return t('attach.empty', { name });
  return null;
}

/**
 * The files of a clipboard or a drop (a DataTransfer-like object): `files`, else the file items.
 * @param {{files?: ArrayLike<File>, items?: ArrayLike<{kind: string, getAsFile?: () => File|null}>}} dt
 * @returns {File[]}
 */
export function filesOf(dt) {
  const files = Array.from(dt?.files || []).filter(Boolean);
  if (files.length) return files;
  const out = [];
  for (const it of Array.from(dt?.items || [])) {
    if (it?.kind !== 'file') continue;
    const f = it.getAsFile?.();
    if (f) out.push(f);
  }
  return out;
}

/** Does a drag carry files? (Also the hooks drop zone of the Studio, web/ui/studio/hooks.js.) */
export function carriesFiles(dt) {
  return Array.from(dt?.types || []).includes('Files');
}

/**
 * Read a file as a data: URL (base64).
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const Reader = globalThis.FileReader;
    const fail = () => reject(new Error(t('attach.readFailed', { name: file?.name || 'image' })));
    if (typeof Reader !== 'function') { fail(); return; }
    const r = new Reader();
    r.onload = () => (typeof r.result === 'string' && r.result ? resolve(r.result) : fail());
    r.onerror = fail;
    r.onabort = fail;
    try {
      r.readAsDataURL(file);
    } catch {
      fail();
    }
  });
}

function previewUrl(file) {
  try {
    const url = globalThis.URL?.createObjectURL?.(file);
    return typeof url === 'string' && url ? url : null;
  } catch {
    return null;
  }
}

function revoke(url) {
  if (!url) return;
  try { globalThis.URL?.revokeObjectURL?.(url); } catch { /* ignore */ }
}

/**
 * The thumbnail of an image to join: its preview (blob: URL), else an image icon.
 * @param {{name: string, url?: string|null}} item
 */
export function thumb(item, cls = 'att-thumb') {
  if (!item.url) return h('span', { class: [cls, 'is-blank'], 'aria-hidden': 'true' }, icon('image', { size: 18 }));
  const img = h('img', { class: cls, src: item.url, alt: '', loading: 'lazy', decoding: 'async' });
  img.addEventListener('error', () => img.classList.add('is-broken'));
  return img;
}

/**
 * The images about to be joined: the 📎 button (`el`, with its hidden file input), the tray of
 * thumbnails (`tray`, × takes one back), and the paste / drop wiring of a composer.
 * @param {{max?: number, read?: (file: File) => Promise<string>, onChange?: (count: number) => void,
 *   onError?: (message: string) => void}} [opts]
 * @returns {{el: HTMLElement, button: HTMLButtonElement, input: HTMLInputElement, tray: HTMLElement,
 *   add(files: ArrayLike<File>): number, remove(id: number): void, clear(): void, count(): number,
 *   items(): object[], take(): object[], restore(items: object[]): void, release(items: object[]): void,
 *   payload(items?: object[]): Promise<{name: string, data: string}[]>, setDisabled(on: boolean): void,
 *   attachPaste(target: HTMLElement): () => void, attachDrop(zone: HTMLElement): () => void, destroy(): void}}
 */
export function createImagePicker(opts = {}) {
  const max = Number.isInteger(opts.max) && opts.max > 0 ? opts.max : MAX_IMAGES;
  const read = typeof opts.read === 'function' ? opts.read : readAsDataUrl;
  let items = [];
  let seq = 0;
  let disabled = false;
  const offs = [];

  const input = h('input', {
    type: 'file', class: 'att-input', accept: IMAGE_ACCEPT, multiple: true, hidden: true, tabIndex: -1, 'aria-hidden': 'true',
  });
  const button = h('button', { type: 'button', class: 'att-btn', 'aria-label': t('attach.add'), title: t('attach.addTitle', { max }) },
    icon('paperclip', { size: 18 }));
  const el = h('span', { class: 'att-pick' }, button, input);
  const tray = h('ul', { class: 'att-tray', hidden: true, 'aria-label': t('attach.trayLabel') });

  function report(message) {
    if (message) opts.onError?.(message);
  }

  function render() {
    tray.replaceChildren(...items.map((it) => h('li', { class: 'att-item', title: it.name },
      thumb(it),
      h('span', { class: 'att-name' }, it.name),
      h('button', {
        type: 'button', class: 'att-remove', 'aria-label': t('attach.remove', { name: it.name }), title: t('attach.remove', { name: it.name }),
        onClick: () => remove(it.id),
      }, icon('close', { size: 12 })))));
    tray.hidden = !items.length;
    button.disabled = disabled;
    button.classList.toggle('is-on', items.length > 0);
    setAttr(button, 'aria-label', items.length ? `${t('attach.add')} (${items.length}/${max})` : t('attach.add'));
    opts.onChange?.(items.length);
  }

  /** Add files (from the chooser, a paste or a drop); returns how many were added. */
  function add(list) {
    if (disabled) return 0;
    let added = 0;
    for (const file of Array.from(list || [])) {
      const problem = imageProblem(file, items.length, max);
      if (problem) {
        report(problem);
        if (items.length >= max) break;
        continue;
      }
      seq += 1;
      items.push({ id: seq, file, name: String(file.name || `image-${seq}`), type: imageTypeOf(file), size: Number(file.size) || 0, url: previewUrl(file) });
      added += 1;
    }
    if (added) render();
    return added;
  }

  function remove(id) {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    revoke(it.url);
    items = items.filter((x) => x !== it);
    render();
    button.focus?.();
  }

  function clear() {
    for (const it of items) revoke(it.url);
    items = [];
    render();
  }

  button.addEventListener('click', () => {
    if (disabled) return;
    if (items.length >= max) { report(t('attach.tooMany', { max })); return; }
    input.click?.();
  });
  input.addEventListener('change', () => {
    add(input.files);
    try { input.value = ''; } catch { /* some browsers refuse: harmless */ }
  });

  return {
    el,
    button,
    input,
    tray,
    add,
    remove,
    clear,
    count: () => items.length,
    /** The images joined so far ({id, name, type, size, url, file}: copies, for payload()). */
    items: () => items.map((it) => ({ ...it })),
    /** The images leave the tray (sent with a message); their previews stay valid until release(). */
    take() {
      const out = items;
      items = [];
      render();
      return out;
    },
    /** Put taken images back (the message was not sent), unless others were joined meanwhile. */
    restore(list) {
      const back = Array.isArray(list) ? list : [];
      if (items.length || !back.length) { for (const it of back) revoke(it.url); return; }
      items = back.slice(0, max);
      render();
    },
    /** Forget taken images for good (their previews are freed). */
    release(list) {
      for (const it of Array.isArray(list) ? list : []) revoke(it.url);
    },
    /** The images as the API wants them: [{name, data}] (data: URL). */
    payload(list = items) {
      return Promise.all((Array.isArray(list) ? list : []).map(async (it) => ({ name: it.name, data: await read(it.file) })));
    },
    setDisabled(on) {
      disabled = !!on;
      button.disabled = disabled;
    },
    /** A pasted screenshot (or copied image file) joins the images; text pastes as usual. */
    attachPaste(target) {
      const onPaste = (e) => {
        if (disabled) return;
        const files = filesOf(e.clipboardData);
        if (!files.length) return;
        if (!Array.from(e.clipboardData?.types || []).includes('text/plain')) e.preventDefault?.();
        add(files);
      };
      target.addEventListener('paste', onPaste);
      const off = () => target.removeEventListener('paste', onPaste);
      offs.push(off);
      return off;
    },
    /** Files dropped on a zone join the images (the zone shows .is-dragover meanwhile). */
    attachDrop(zone) {
      const over = (e) => {
        if (disabled || !carriesFiles(e.dataTransfer)) return;
        e.preventDefault?.();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        zone.classList.add('is-dragover');
      };
      const leave = () => zone.classList.remove('is-dragover');
      const drop = (e) => {
        zone.classList.remove('is-dragover');
        if (disabled || !carriesFiles(e.dataTransfer)) return;
        e.preventDefault?.();
        add(filesOf(e.dataTransfer));
      };
      zone.addEventListener('dragover', over);
      zone.addEventListener('dragleave', leave);
      zone.addEventListener('drop', drop);
      const off = () => {
        zone.removeEventListener('dragover', over);
        zone.removeEventListener('dragleave', leave);
        zone.removeEventListener('drop', drop);
      };
      offs.push(off);
      return off;
    },
    destroy() {
      for (const off of offs.splice(0)) { try { off(); } catch { /* ignore */ } }
      for (const it of items) revoke(it.url);
      items = [];
    },
  };
}
